import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import PageTitle from "../../layouts/PageTitle";
import {
  deleteStudyProgram,
  getStudyProgram,
  uploadStudyProgram,
  getDeletedStudyPrograms,
  restoreStudyProgram,
  hardDeleteStudyProgram,
} from "../../../services/StudyProgramService";
import Swal from "sweetalert2";

const StudyProgram = () => {
  const location = useLocation();
  const s = location.state || {};

  const [open, setOpen] = useState(true);
  const [open2, setOpen2] = useState(true);
  const hasFetched = useRef(false);
  const [loading, setLoading] = useState(false);
  const [studyPrograms, setStudyPrograms] = useState([]);
  const [searchTerm, setSearchTerm] = useState(s.searchTerm || "");
  const [page, setPage] = useState(s.page || 1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Recycle Bin state — initialize from location.state to avoid flicker on return
  const [showTrash, setShowTrash] = useState(!!s.showTrash);
  const [trashPrograms, setTrashPrograms] = useState([]);
  const [trashSearchTerm, setTrashSearchTerm] = useState(s.trashSearchTerm || "");
  const [trashPage, setTrashPage] = useState(s.trashPage || 1);
  const [trashTotalPages, setTrashTotalPages] = useState(0);
  const [trashTotalItems, setTrashTotalItems] = useState(0);
  const [trashLoading, setTrashLoading] = useState(false);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const allowedTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      Swal.fire("Error", "Only CSV or Excel files allowed", "error");
      return;
    }

    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      await uploadStudyProgram(formData);

      Swal.fire("Success", "Study Program uploaded successfully", "success");

      fetchStudyPrograms(page, searchTerm);

    } catch (error) {

      Swal.fire("Error", "Upload failed", "error");

    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const fetchStudyPrograms = async (pageNumber = 1, query = "") => {
    try {
      setLoading(true);
      const response = await getStudyProgram(pageNumber, limit, query);
      const { data, pagination } = response.data;
      setStudyPrograms(data || []);
      setTotalPages(pagination?.totalPages || 0);
      setTotalItems(pagination?.totalItems || 0);
      setPage(pagination?.currentPage || 1);
    } catch (error) {
      console.error("Error fetching study programs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedPrograms = async (pageNumber = 1, query = "") => {
    try {
      setTrashLoading(true);
      const response = await getDeletedStudyPrograms(pageNumber, limit, query);
      const { data, pagination } = response.data;
      setTrashPrograms(data || []);
      setTrashTotalPages(pagination?.totalPages || 0);
      setTrashTotalItems(pagination?.totalItems || 0);
      setTrashPage(pagination?.currentPage || 1);
    } catch (error) {
      console.error("Error fetching deleted study programs:", error);
    } finally {
      setTrashLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchStudyPrograms(s.page || 1, s.searchTerm || "");
      if (s.showTrash) {
        fetchDeletedPrograms(s.trashPage || 1, s.trashSearchTerm || "");
      }
    }
  }, []);

  const handleOpenTrash = () => {
    setShowTrash(true);
    setTrashSearchTerm("");
    setTrashPage(1);
    fetchDeletedPrograms(1, "");
  };

  const handleCloseTrash = () => {
    setShowTrash(false);
  };

  const handleSearch = () => {
    setPage(1);
    fetchStudyPrograms(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setPage(1);
    fetchStudyPrograms(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
      fetchStudyPrograms(newPage, searchTerm);
    }
  };

  const handleTrashSearch = () => {
    setTrashPage(1);
    fetchDeletedPrograms(1, trashSearchTerm);
  };

  const handleTrashReset = () => {
    setTrashSearchTerm("");
    setTrashPage(1);
    fetchDeletedPrograms(1, "");
  };

  const handleTrashPageChange = (newPage) => {
    if (newPage > 0 && newPage <= trashTotalPages) {
      setTrashPage(newPage);
      fetchDeletedPrograms(newPage, trashSearchTerm);
    }
  };

  const handleDeleteStudyProgram = (programId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this study program!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dd6b55",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteStudyProgram(programId);
          Swal.fire("Deleted!", "Study program has been deleted.", "success");
          fetchStudyPrograms(page, searchTerm);
        } catch (error) {
          Swal.fire("Error!", "Failed to delete study program.", "error");
        }
      }
    });
  };

  const handleRestore = (programId) => {
    Swal.fire({
      title: "Restore Study Program?",
      text: "This study program will be restored to the active list.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, restore it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await restoreStudyProgram(programId);
          Swal.fire("Restored!", "Study program has been restored.", "success");
          fetchDeletedPrograms(trashPage, trashSearchTerm);
          fetchStudyPrograms(page, searchTerm);
        } catch (error) {
          Swal.fire("Error!", "Failed to restore study program.", "error");
        }
      }
    });
  };

  const handleHardDelete = (programId) => {
    Swal.fire({
      title: "Permanently Delete?",
      text: "This action cannot be undone. The study program will be permanently removed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dd6b55",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, delete permanently!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await hardDeleteStudyProgram(programId);
          Swal.fire("Deleted!", "Study program has been permanently deleted.", "success");
          fetchDeletedPrograms(trashPage, trashSearchTerm);
        } catch (error) {
          Swal.fire("Error!", "Failed to permanently delete study program.", "error");
        }
      }
    });
  };

  const renderPagination = (currentPage, totalPgs, totalItms, onPageChange) => (
    totalPgs > 0 && (
      <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
        <div className="dataTables_info">
          {totalItms > 0 && (
            <>
              Showing {(currentPage - 1) * limit + 1} to{" "}
              {Math.min(currentPage * limit, totalItms)} of{" "}
              {totalItms} entries
            </>
          )}
        </div>
        <div
          className="dataTables_paginate paging_simple_numbers"
          id="example2_paginate"
        >
          <Link
            className={`paginate_button previous ${currentPage === 1 ? "disabled" : ""}`}
            to="#"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage > 1) onPageChange(currentPage - 1);
            }}
          >
            <i className="fa fa-angle-double-left" />
          </Link>

          <span>
            {Array.from({ length: totalPgs }, (_, i) => i + 1)
              .filter((i) => {
                if (i <= 2 || i > totalPgs - 2) return true;
                if (Math.abs(currentPage - i) <= 1) return true;
                return false;
              })
              .reduce((acc, i, index, arr) => {
                if (index > 0 && i - arr[index - 1] > 1) acc.push("ellipsis");
                acc.push(i);
                return acc;
              }, [])
              .map((item, index) =>
                item === "ellipsis" ? (
                  <span key={index} className="paginate_button disabled">
                    ...
                  </span>
                ) : (
                  <Link
                    key={index}
                    to="#"
                    className={`paginate_button ${currentPage === item ? "current" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onPageChange(item);
                    }}
                  >
                    {item}
                  </Link>
                )
              )}
          </span>

          <Link
            className={`paginate_button next ${currentPage === totalPgs ? "disabled" : ""}`}
            to="#"
            onClick={(e) => {
              e.preventDefault();
              if (currentPage < totalPgs) onPageChange(currentPage + 1);
            }}
          >
            <i className="fa fa-angle-double-right" />
          </Link>
        </div>
      </div>
    )
  );

  return (
    <>
      <PageTitle
        activeMenu="Study Program"
        motherMenu="Study Program"
        activeMenuLink={"study-program"}
        motherMenuLink={"study-program"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="filter cm-content-box box-primary">
            <div className={`content-title`} onClick={() => setOpen(!open)}>
              <div className="cpa">
                <i className="fas fa-filter me-2"></i>Filter
              </div>
              <div className="tools">
                <Link
                  to={"#"}
                  className={`SlideToolHeader ${open ? "collapse" : "expand"}`}
                >
                  <i className="fas fa-angle-up"></i>
                </Link>
              </div>
            </div>
            <Collapse in={open}>
              <div className="cm-content-body form excerpt">
                <div className="card-body">
                  <div className="row">
                    <div className="col-xl-3 col-sm-6">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control mb-3 mb-xl-0"
                        placeholder="Title"
                        value={showTrash ? trashSearchTerm : searchTerm}
                        onChange={(e) =>
                          showTrash
                            ? setTrashSearchTerm(e.target.value)
                            : setSearchTerm(e.target.value)
                        }
                      />
                    </div>
                    <div className="col-xl-3 col-sm-6 align-self-end">
                      <button
                        className="btn btn-primary me-2 rounded-sm"
                        title="Click here to Search"
                        type="button"
                        onClick={showTrash ? handleTrashSearch : handleSearch}
                      >
                        <i className="fa fa-search me-1"></i>Filter
                      </button>
                      <button
                        className="btn btn-danger light rounded-sm"
                        title="Click here to remove filter"
                        type="button"
                        onClick={showTrash ? handleTrashReset : handleReset}
                      >
                        <i className="fa fa-undo me-1"></i>
                        Remove Filter
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
          <div className="mb-4 pb-3">
            <ul className="d-flex align-items-center flex-wrap gap-2">
              {!showTrash ? (
                <>
                  <li>
                    <Link
                      to={"/add-study-program"}
                      className="btn btn-sm btn-primary"
                    >
                      Add Study Program
                    </Link>
                  </li>
                  <li>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={() => fileInputRef.current.click()}
                      disabled={uploading}
                    >
                      <i className="fa fa-upload me-1"></i>
                      {uploading ? "Uploading..." : "Upload Study programs"}
                    </button>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleFileUpload}
                    />
                  </li>
                  <li>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleOpenTrash}
                    >
                      <i className="fa-solid fa-trash-can me-1"></i>
                      Recycle Bin
                    </button>
                  </li>
                </>
              ) : (
                <li>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={handleCloseTrash}
                  >
                    <i className="fa fa-arrow-left me-1"></i>
                    Back to List
                  </button>
                </li>
              )}
            </ul>
          </div>
          <div className="filter cm-content-box box-primary">
            <div className={`content-title`} onClick={() => setOpen2(!open2)}>
              <div className="cpa">
                {showTrash ? (
                  <>
                    <i className="fa-solid fa-trash-can me-2" />
                    Recycle Bin
                  </>
                ) : (
                  <>
                    <i className="fas fa-graduation-cap me-1" />
                    Study Program List
                  </>
                )}
              </div>
              <div className="tools">
                <Link
                  to={"#"}
                  className={`SlideToolHeader ${open2 ? "collapse" : "expand"}`}
                >
                  <i className="fas fa-angle-up"></i>
                </Link>
              </div>
            </div>
            <Collapse in={open2}>
              <div className="cm-content-body form excerpt">
                <div className="card-body py-3">
                  {(showTrash ? trashLoading : loading) ? (
                    <p>Loading...</p>
                  ) : (
                    <div className="table-responsive order-list-table">
                      <div
                        id="blog_wrapper"
                        className="dataTables_wrapper no-footer"
                      >
                        <table className="table table-responsive-lg table-condensed flip-content">
                          <thead>
                            <tr>
                              <th className="text-black">S.No</th>
                              <th className="text-black">Course Name</th>
                              <th className="text-black">University</th>
                              <th className="text-black">Degree</th>
                              <th className="text-black">Application</th>
                              <th className="text-black">Country</th>
                              <th className="text-black">Created</th>
                              <th className="text-black text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(showTrash ? trashPrograms : studyPrograms).length > 0 ? (
                              (showTrash ? trashPrograms : studyPrograms).map((item, ind) => (
                                <tr key={ind}>
                                  <td>{((showTrash ? trashPage : page) - 1) * limit + ind + 1}</td>
                                  <td className="truncate">{item.course_name}</td>
                                  <td className="truncate">{item.university_name || "-"}</td>
                                  <td className="truncate">{item.degree_name || "-"}</td>
                                  <td className="truncate">{item.application_name || "-"}</td>
                                  <td className="truncate">{item.country_names || "-"}</td>
                                  <td>
                                    {new Date(item.createdAt).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}
                                  </td>
                                  <td className="text-end">
                                    {showTrash ? (
                                      <>
                                        <Link
                                          to={`/view-study-program/${item.id}`}
                                          state={{ page, searchTerm, showTrash: true, trashPage, trashSearchTerm }}
                                          className="btn btn-info btn-sm content-icon me-1"
                                          title="View"
                                        >
                                          <i className="fa fa-eye"></i>
                                        </Link>
                                        <button
                                          className="btn btn-success btn-sm content-icon me-1"
                                          title="Restore"
                                          onClick={() => handleRestore(item.id)}
                                        >
                                          <i className="fa-solid fa-rotate-left"></i>
                                        </button>
                                        <button
                                          className="btn btn-danger btn-sm content-icon ms-1"
                                          title="Delete Permanently"
                                          onClick={() => handleHardDelete(item.id)}
                                        >
                                          <i className="fa-solid fa-trash"></i>
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <Link
                                          to={`/edit-study-program/${item.id}`}
                                          state={{ page, searchTerm, showTrash: false }}
                                          className="btn btn-warning btn-sm content-icon me-1"
                                        >
                                          <i className="fa fa-edit"></i>
                                        </Link>
                                        <Link
                                          to={"#"}
                                          className="btn btn-danger btn-sm content-icon ms-1"
                                          onClick={() => handleDeleteStudyProgram(item.id)}
                                        >
                                          <i className="fa-solid fa-trash" />
                                        </Link>
                                      </>
                                    )}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center">
                                  {showTrash ? "No deleted study programs found" : "No study programs found"}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        {showTrash
                          ? renderPagination(trashPage, trashTotalPages, trashTotalItems, handleTrashPageChange)
                          : renderPagination(page, totalPages, totalItems, handlePageChange)
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Collapse>
          </div>
        </div>
      </div>
    </>
  );
};
export default StudyProgram;
